import {
  composeDocumentsTableRowsWithExpansion,
  getMatchingFolderFiles,
  groupDocumentFilesByFolderId,
} from '@monorepo/shared/documents/composeDocumentsTableRows';
import {
  DocumentsFileRow,
  DocumentsFolderRow,
  DocumentsTableRow,
} from '@monorepo/shared/documents/types';
import { DocumentFileResponse } from 'mapistry-shared';
import { useCallback, useMemo } from 'react';
import { useExpandedFolderIds } from './useExpandedFolderIds';

type UseInlineFolderExpansionParams = {
  organizationId: string;
  projectId?: string;
  files: DocumentFileResponse[] | undefined;
  searchTerm?: string;
};

export function useInlineFolderExpansion({
  organizationId,
  projectId,
  files,
  searchTerm,
}: UseInlineFolderExpansionParams) {
  const filesByFolderId = useMemo(
    () => groupDocumentFilesByFolderId(files),
    [files],
  );

  const folderIdsWithMatchingFiles = useMemo(() => {
    const ids = new Set<string>();
    if (!searchTerm?.trim()) return ids;
    filesByFolderId.forEach((folderFiles, folderId) => {
      if (getMatchingFolderFiles(folderFiles, searchTerm).length) {
        ids.add(folderId);
      }
    });
    return ids;
  }, [filesByFolderId, searchTerm]);

  const { expandedFolderIds, toggleFolder } = useExpandedFolderIds({
    organizationId,
    projectId,
    autoExpandedFolderIds: folderIdsWithMatchingFiles,
  });

  const composeDisplayRows = useCallback(
    (
      topLevelRows: Array<DocumentsFolderRow | DocumentsFileRow>,
    ): DocumentsTableRow[] =>
      composeDocumentsTableRowsWithExpansion({
        topLevelRows,
        expandedFolderIds,
        filesByFolderId,
        searchTerm,
      }),
    [expandedFolderIds, filesByFolderId, searchTerm],
  );

  return {
    expandedFolderIds,
    folderIdsWithMatchingFiles,
    toggleFolder,
    composeDisplayRows,
  };
}
